import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from './Header';
import ImageSection from './ImageSection';
import QuickActions from './QuickActions';
import DetailTabs from './DetailTabs';
import InfoTab from './tabs/InfoTab';
import HistoryTab from './tabs/HistoryTab';
import SalesTab from './tabs/SalesTab';
import { useLots } from '../../../../contexts/LotsContext';
import { useSales } from '../../../../contexts/SalesContext';
import LightningLotIndicator from '../LightningLotIndicator';
import ReconcileFloatingButton from '../ReconcileFloatingButton';
import ReconcileModal from '../modals/ReconcileModal';
import ConstructionModal from '../../../shared/modals/ConstructionModal';

const formatDateTime = (isoString) => {
  const date = new Date(isoString);
  return {
    date: date.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }),
    time: date.toLocaleTimeString('es-AR', {
      hour: '2-digit',
      minute: '2-digit'
    })
  };
};

const LotDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lots, updateLot } = useLots();
  const { sales } = useSales();
  const [activeTab, setActiveTab] = useState('info');
  const [showReconcileModal, setShowReconcileModal] = useState(false);
  const [showConstructionModal, setShowConstructionModal] = useState(false);

  // Encontrar el lote por ID
  const lot = lots.find(l => l.id === id);

  // Logging para debugging
  console.log('Lote original encontrado:', lot);

  // Si no se encuentra el lote, redirigir al inventario
  if (!lot) {
    navigate('/', { state: { activeScreen: 'inventory' } });
    return null;
  }

  const handleImageUpload = (e) => {
    e.preventDefault();
    setShowConstructionModal(true);
  };

  const handleCameraClick = () => {
    setShowConstructionModal(true);
  };

  const { date: createdDate, time: createdTime } = formatDateTime(lot.timestamp);

  // Encontrar las ventas relacionadas con este lote
  const lotSales = sales
    .filter(sale => sale.items.some(item => item.lotId === lot.id))
    .map(sale => ({
      id: sale.id,
      client: sale.customer_name,
      quantity: sale.items.find(item => item.lotId === lot.id).quantity,
      price: sale.items.find(item => item.lotId === lot.id).unit_price,
      timestamp: formatDateTime(sale.created_at).time
    }))
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  // Adaptar los datos del lote al formato esperado por los componentes
  const adaptedLot = {
    id: lot.id,
    number: lot.id.replace('L', ''), // Número de lote sin el prefijo
    title: lot.title || lot.name,
    display_id: lot.display_id,
    currentQuantity: Number(lot.remaining_quantity || lot.quantity),
    unit: lot.unit_sign || lot.unit,
    images: lot.image_url ? [lot.image_url] : [],
    minPrice: lot.unit_price || 0,
    createdAt: lot.created_at || lot.timestamp,
    createdDate,
    createdTime,
    provider: lot.supplier_name || lot.supplier,
    observation: lot.observation,
    initialQuantity: lot.status === 'lightning' ? null : Number(lot.initial_quantity !== undefined ? lot.initial_quantity : lot.quantity),
    status: lot.status,
    lastUpdate: {
      timestamp: formatDateTime(lot.updated_at || lot.timestamp).time
    },
    movements: [
      {
        type: 'add',
        user: 'Sistema',
        timestamp: formatDateTime(lot.created_at || lot.timestamp).time,
        description: 'Creación del lote',
        quantity: lot.initial_quantity || lot.quantity,
        unit: lot.unit_sign || lot.unit
      },
      ...(lot.movements || []).map(m => ({
        type: m.type,
        user: m.auth_user_id || 'Sistema',
        timestamp: formatDateTime(m.created_at || m.timestamp).time,
        description: m.note,
        quantity: m.quantity,
        unit: m.unit_sign || m.unit,
        previous_remaining_quantity: m.previous_remaining_quantity,
        new_remaining_quantity: m.new_remaining_quantity
      }))
    ],
    recentSales: lotSales
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'info':
        return <InfoTab lot={adaptedLot} />;
      case 'historial':
        return <HistoryTab lot={adaptedLot} />;
      case 'ventas':
        return <SalesTab lot={adaptedLot} />;
      default:
        return <InfoTab lot={adaptedLot} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24">
      <Header lot={adaptedLot} />
      <div className="px-4">
        <LightningLotIndicator lot={lot} />
      </div>
      {adaptedLot.images.length > 0 && (
        <ImageSection 
          images={adaptedLot.images} 
          title={adaptedLot.title} 
          onImageUpload={handleImageUpload}
          onCameraClick={handleCameraClick}
        />
      )}
      <QuickActions lot={adaptedLot} />
      <DetailTabs activeTab={activeTab} onTabChange={setActiveTab} />
      {renderTabContent()}
      <ReconcileFloatingButton 
        lot={lot} 
        onClick={() => setShowReconcileModal(true)} 
      />
      
      {adaptedLot.images.length === 0 && (
        <div className="mt-6">
          <ImageSection 
            images={adaptedLot.images} 
            title={adaptedLot.title} 
            onImageUpload={handleImageUpload}
            onCameraClick={handleCameraClick}
          />
        </div>
      )}

      {/* Modal de reconciliación */}
      {showReconcileModal && (
        <ReconcileModal
          lot={{
            ...lot,
            // Asegurar que remaining_quantity tiene el valor negativo correcto
            // Si no lo tiene directamente, tomarlo de la propiedad adaptada currentQuantity
            remaining_quantity: lot.remaining_quantity || -1 * Math.abs(adaptedLot.currentQuantity)
          }}
          onClose={() => setShowReconcileModal(false)}
        />
      )}
      
      {/* Modal de construcción para la carga de imágenes */}
      <ConstructionModal
        isOpen={showConstructionModal}
        onClose={() => setShowConstructionModal(false)}
        title="Carga de Imágenes"
        message="La funcionalidad de carga de imágenes está siendo mejorada. ¡Pronto podrás agregar fotos a tus lotes!"
        buttonText="Entendido"
      />
    </div>
  );
};

export default LotDetail; 